/**
 * Bots.jsx — default screen.
 *
 * Balance and P&L in one glance, then the list. Someone opening this on a
 * phone wants to know what their bots are doing, not to browse.
 *
 * No charts anywhere. That is what keeps recharts out of the bundle, and on
 * a 375px screen three numbers read better than a sparkline.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import * as tg from "../lib/telegram";
import { C, money, pnlColor, Label, Panel, Button, Empty, Chip } from "../theme";

// A test account here has 346 bots. Rendering all of them produces a scroll
// nobody finishes, so show the ones that matter and let the rest be opened
// deliberately.
const INITIAL_VISIBLE = 12;

export default function Bots({ onOpen, onCreate, onAccount, onDeposit }) {
  const [bots, setBots] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [b, w] = await Promise.all([
          api.listBots(),
          api.balance().catch(() => null),   // balance is nice-to-have
        ]);
        setBots(Array.isArray(b) ? b : b?.bots || []);
        if (w) setWallet(w);
        setErr(null);
      } catch (e) {
        setErr(e.message);
      }
    };
    load();
    return api.poll(load, 10000);
  }, []);

  if (err && !bots) return <Empty><span style={{ color: C.red }}>{err}</span></Empty>;
  if (!bots) return <Empty>Loading…</Empty>;

  const live    = bots.filter((b) => b.mode === "live");
  const paper   = bots.filter((b) => b.mode === "paper");
  const running = bots.filter((b) => b.status === "running");

  // NOTE: total_profit_loss, not pnl — that is the copybot API's field name.
  const total = bots.reduce((s, b) => s + Number(b.total_profit_loss || 0), 0);

  // Running first, then anything with activity, then the rest. A stopped bot
  // with zero trades is the least interesting thing on the screen.
  const ranked = [...bots].sort((a, b) => {
    const ar = a.status === "running" ? 0 : 1;
    const br = b.status === "running" ? 0 : 1;
    if (ar !== br) return ar - br;
    return (b.total_copied_trades || 0) - (a.total_copied_trades || 0);
  });
  const visible = showAll ? ranked : ranked.slice(0, INITIAL_VISIBLE);

  const bal = wallet
    ? Number(wallet.balance ?? wallet.usdc ?? wallet.available ?? 0)
    : null;

  return (
    <div style={{ padding: 14 }}>

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", marginBottom: 14, gap: 10 }}>
        <div onClick={onDeposit} style={{ cursor: "pointer" }}>
          <Label>BALANCE</Label>
          <div style={{ fontFamily: C.disp, fontSize: 30, color: C.ink, lineHeight: 1 }}>
            {bal === null ? "—" : money(bal, false)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <Label>TOTAL P&amp;L</Label>
          <div style={{ fontFamily: C.disp, fontSize: 30, lineHeight: 1,
                        color: pnlColor(total) }}>
            {money(total)}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <Chip color={running.length ? C.green : C.dim}>{running.length} RUNNING</Chip>
        <Chip>{live.length} LIVE</Chip>
        <Chip>{paper.length} PAPER</Chip>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: 7, marginBottom: 18 }}>
        <Button onClick={onCreate}>+ New bot</Button>
        <Button variant="ghost" onClick={onAccount}>Account</Button>
      </div>

      <Label>YOUR BOTS</Label>

      {bots.length === 0 && (
        <Empty>
          No bots yet.
          <div style={{ marginTop: 16 }}>
            <Button onClick={onCreate}>Create your first bot</Button>
          </div>
        </Empty>
      )}

      {visible.map((b) => {
        const pnl = Number(b.total_profit_loss || 0);
        const accent = b.status === "running"
          ? (pnl < 0 ? C.red : C.green)
          : C.dim;
        return (
          <Panel key={b.bot_id} accent={accent} onClick={() => onOpen(b.bot_id)}
                 style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "baseline", marginBottom: 5, gap: 8 }}>
              <span style={{ fontSize: 13, color: C.ink, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {b.bot_name || b.trader_name || b.bot_id}
              </span>
              <span style={{ fontSize: 14, color: pnlColor(pnl), fontWeight: 500,
                             flexShrink: 0 }}>
                {money(pnl)}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, fontSize: 11, color: C.dim,
                          flexWrap: "wrap" }}>
              <span>{String(b.mode || "").toUpperCase()}</span>
              <span>· {b.status}</span>
              <span>· {b.total_copied_trades || 0} trades</span>
              {b.win_rate ? <span>· {Number(b.win_rate).toFixed(0)}% win</span> : null}
            </div>
          </Panel>
        );
      })}

      {!showAll && ranked.length > INITIAL_VISIBLE && (
        <Button variant="quiet" onClick={() => setShowAll(true)}
                style={{ marginTop: 4 }}>
          Show all {ranked.length} bots
        </Button>
      )}

      {/* Fee transparency. On the free tier the user pays 1% per trade, so
          showing what that costs is honest — and it becomes the upgrade
          prompt without being a purchase CTA, which Telegram's rules on
          digital goods would not permit. */}
      <div style={{ border: `0.5px solid ${C.border}`,
                    background: "rgba(245,166,35,0.04)", padding: "11px 12px",
                    marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: C.dim }}>Taker fee</span>
          <span style={{ fontSize: 12, color: C.ink }}>1.0%</span>
        </div>
        <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.6 }}>
          Free tier. A paid plan removes it — plans are managed at
          polyedge.online.
        </p>
      </div>
    </div>
  );
}
